// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../../../../../contexts/ProjectContext';
import {
  FiLoader as Loader2, FiRefreshCw as RefreshCw, FiFilter as Filter,
  FiDownload as Download, FiClipboard as ClipboardList,
  FiChevronDown as ChevronDown, FiChevronUp as ChevronUp
} from 'react-icons/fi';
import { getAuditReport } from '../../../../../services/aiOperationsApi';
import { ProjectBadge, formatUserName } from '../GovernancePage';

const AuditLogContent = () => {
  const { projects } = useProject();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [timePreset, setTimePreset] = useState('last_30_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const [exporting, setExporting] = useState(false);

  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    switch (timePreset) {
      case 'today':
        return { start: today.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      case 'last_7_days': {
        const week = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
        return { start: week.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      }
      case 'last_30_days': {
        const month = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
        return { start: month.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      }
      case 'last_90_days': {
        const quarter = new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000);
        return { start: quarter.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      }
      case 'this_year': {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return { start: yearStart.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      }
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      case 'all_time':
      default:
        return { start: '', end: '' };
    }
  };

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      let action = actionFilter || undefined;
      if (typeFilter === 'releases') {
        action = 'promote';
      } else if (typeFilter === 'approvals') {
        action = actionFilter || undefined;
        if (!actionFilter) action = undefined;
      }

      const dateRange = getDateRange();

      const data = await getAuditReport({
        action,
        resource_type: resourceTypeFilter || undefined,
        start_date: dateRange.start || undefined,
        end_date: dateRange.end || undefined,
        page,
        limit: pageSize,
        format: 'json'
      });

      let filteredLogs = data.logs || [];

      if (typeFilter === 'approvals') {
        filteredLogs = filteredLogs.filter(log => ['approve', 'reject'].includes(log.action));
      } else if (typeFilter === 'changes') {
        filteredLogs = filteredLogs.filter(log => ['create', 'update', 'delete'].includes(log.action));
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredLogs = filteredLogs.filter(log =>
          log.resource_name?.toLowerCase().includes(term) ||
          log.resource_id?.toLowerCase().includes(term) ||
          log.user_id?.toLowerCase().includes(term)
        );
      }

      setLogs(filteredLogs);
      setTotalCount(data.total || filteredLogs.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter, resourceTypeFilter, typeFilter, searchTerm, timePreset, customStartDate, customEndDate, page, pageSize]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    setPage(1);
  }, [actionFilter, resourceTypeFilter, typeFilter, searchTerm, timePreset, customStartDate, customEndDate, pageSize]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const data = await getAuditReport({
        action: actionFilter || undefined,
        resource_type: resourceTypeFilter || undefined,
        limit: 1000,
        format: 'json'
      });

      const logsToExport = data.logs || [];
      const dateStr = new Date().toISOString().split('T')[0];

      if (format === 'csv') {
        const headers = ['Timestamp', 'Action', 'Resource Type', 'Resource ID', 'Resource Name', 'User', 'Details'];
        const rows = logsToExport.map(log => [
          new Date(log.timestamp).toISOString(),
          log.action,
          log.resource_type,
          log.resource_id,
          log.resource_name || '',
          formatUserName(log.user_id),
          JSON.stringify(log.details || {})
        ]);
        const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replaceAll('"', '""')}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_${dateStr}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const json = JSON.stringify(logsToExport, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_${dateStr}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const actionColors = {
    create: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    delete: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    promote: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    approve: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    reject: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && logs.length === 0) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {[
            { value: '', label: 'All' },
            { value: 'releases', label: 'Releases' },
            { value: 'approvals', label: 'Approvals' },
            { value: 'changes', label: 'Changes' }
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setTypeFilter(opt.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                typeFilter === opt.value
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm"
          disabled={typeFilter === 'releases'}
        >
          <option value="">All Actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="promote">Promote</option>
          <option value="approve">Approve</option>
          <option value="reject">Reject</option>
        </select>

        <select
          value={resourceTypeFilter}
          onChange={(e) => setResourceTypeFilter(e.target.value)}
          className="rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm"
        >
          <option value="">All Resources</option>
          <option value="prompt">Prompts</option>
          <option value="dataset">Datasets</option>
          <option value="api_key">API Keys</option>
        </select>

        <select
          value={timePreset}
          onChange={(e) => setTimePreset(e.target.value)}
          className="rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm"
        >
          <option value="today">Today</option>
          <option value="last_7_days">Last 7 days</option>
          <option value="last_30_days">Last 30 days</option>
          <option value="last_90_days">Last 90 days</option>
          <option value="this_year">This year</option>
          <option value="all_time">All time</option>
          <option value="custom">Custom range</option>
        </select>

        {timePreset === 'custom' && (
          <>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Start date"
            />
            <span className="text-gray-500 text-sm">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="End date"
            />
          </>
        )}

        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by resource name..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>

        <div className="relative group">
          <button
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export
            <ChevronDown className="w-3 h-3" />
          </button>
          <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
            <button
              onClick={() => handleExport('csv')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
            >
              Export CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
            >
              Export JSON
            </button>
          </div>
        </div>

        <button
          onClick={loadLogs}
          className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Show</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-gray-600 dark:text-gray-400">entries</span>
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {totalCount} total records
        </span>
      </div>

      {logs.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-12 text-center">
          <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Audit Logs</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No logs match your search criteria' : 'No audit logs found for the selected filters'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Resource</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">User</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpandedRow(expandedRow === log.id ? null : log.id); }} role="row" tabIndex={0}
                  >
                    <td className="px-4 py-4 text-gray-500">
                      {expandedRow === log.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${actionColors[log.action] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="flex flex-col">
                        <span className="text-gray-900 dark:text-white font-medium">
                          {log.resource_name || log.resource_type}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {log.resource_type} #{log.resource_id?.slice(0, 8)}
                        </span>
                        <div className="mt-1">
                          <ProjectBadge name={log.project_id ? (projects.find((project) => project.id === log.project_id)?.name ?? 'Project') : null} />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                      {formatUserName(log.user_id)}
                    </td>
                  </tr>
                  {expandedRow === log.id && (
                    <tr className="bg-gray-50 dark:bg-gray-900/50">
                      <td colSpan={5} className="px-4 py-4">
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Resource ID</span>
                              <p className="text-gray-900 dark:text-white font-mono text-xs mt-1">{log.resource_id}</p>
                            </div>
                            {log.environment && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Environment</span>
                                <p className="text-gray-900 dark:text-white mt-1">{log.environment}</p>
                              </div>
                            )}
                            {log.version_number && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Version</span>
                                <p className="text-gray-900 dark:text-white mt-1">v{log.version_number}</p>
                              </div>
                            )}
                            {log.pass_rate != null && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Pass Rate</span>
                                <p className="text-gray-900 dark:text-white mt-1">{(log.pass_rate * 100).toFixed(1)}%</p>
                              </div>
                            )}
                          </div>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <div>
                              <span className="text-gray-500 dark:text-gray-400 text-xs uppercase">Details</span>
                              <pre className="mt-1 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogContent;
